import {
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  CSSProperties
} from 'react'
import clsx from 'clsx'
import Image, { ImageProps } from 'next/image'

import { FontSizeOptions } from 'shared/ui/utils/designTokens'

import styles from './styles.module.scss'

export const PageTitle = ({
  className,
  ...props
}: ComponentPropsWithRef<'h1'>) => (
  <h1 className={clsx(styles.pageTitle, className)} {...props} />
)

type SectionTitleProps = ComponentPropsWithRef<'h2'> & {
  $center?: boolean
  $size?: FontSizeOptions
  $lineHeight?: number
}

export const SectionTitle = ({
  $center = false,
  $size = '2xl',
  $lineHeight,
  className,
  style,
  ...props
}: SectionTitleProps) => (
  <h2
    className={clsx(
      styles.sectionTitle,
      styles[`size-${$size}`],
      { [styles.center]: $center },
      className
    )}
    style={
      $lineHeight === undefined
        ? style
        : ({
            '--section-title-line-height': $lineHeight,
            ...style
          } as CSSProperties)
    }
    {...props}
  />
)

type SectionContainerProps = ComponentPropsWithoutRef<'section'> & {
  as?: 'section' | 'div'
  $desktop?: boolean
  $tablet?: boolean
  $mobile?: boolean
  $condensed?: boolean
}

export const SectionContainer = ({
  as: Component = 'section',
  $desktop = true,
  $tablet = true,
  $mobile = true,
  $condensed = false,
  className,
  ...props
}: SectionContainerProps) => (
  <Component
    className={clsx(
      styles.sectionContainer,
      {
        [styles.noDesktopPadding]: !$desktop,
        [styles.condensed]: $condensed,
        [styles.tabletPadding]: $tablet,
        [styles.noTabletPadding]: !$tablet,
        [styles.mobilePadding]: $mobile,
        [styles.noMobilePadding]: !$mobile
      },
      className
    )}
    {...props}
  />
)

export const InfoBox = ({
  className,
  ...props
}: ComponentPropsWithRef<'div'>) => (
  <div className={clsx(styles.infoBox, className)} {...props} />
)

export const BannerImage = ({ className, alt, ...props }: ImageProps) => (
  <Image className={clsx(styles.bannerImage, className)} alt={alt} {...props} />
)

export type SectionContentPadding =
  | 0
  | 2
  | 3
  | 4
  | 6
  | 8
  | 10
  | 12
  | 16
  | 20
  | 24

type SectionContentProps = ComponentPropsWithRef<'div'> & {
  $top?: SectionContentPadding
  $bottom?: SectionContentPadding
  $topMobile?: SectionContentPadding
  $bottomMobile?: SectionContentPadding
}

export const SectionContent = ({
  $top = 6,
  $bottom = 6,
  $topMobile = 2,
  $bottomMobile = 2,
  className,
  ...props
}: SectionContentProps) => (
  <div
    className={clsx(
      styles[`top${$top}`],
      styles[`bottom${$bottom}`],
      styles[`topMobile${$topMobile}`],
      styles[`bottomMobile${$bottomMobile}`],
      className
    )}
    {...props}
  />
)
